import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import './InteractiveMap.css';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import { Box, Input, VStack, HStack, Text, Spinner, Alert, AlertIcon } from '@chakra-ui/react';
import { Search, MapPin, Navigation } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { formatShortAddress, reverseGeocode, NominatimAddress } from '../../../../services/GeocodingService';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface InteractiveMapProps {
  latitude: number;
  longitude: number;
  onLocationChange: (lat: number, lng: number, address?: string) => void;
  height?: string;
  showSearch?: boolean;
  hideLocationButton?: boolean;
}

interface InteractiveMapRef {
  triggerAutoLocation: () => void;
}

interface SearchResult {
  lat: number;
  lon: number;
  display_name: string;
  address?: NominatimAddress;
}

// Component to handle map clicks
const MapClickHandler: React.FC<{
  onLocationChange: (lat: number, lng: number, address?: string) => void
}> = ({ onLocationChange }) => {
  useMapEvents({
    click: async (e) => {
      const { lat, lng } = e.latlng;
      const result = await reverseGeocode(lat, lng);
      onLocationChange(lat, lng, result.shortAddress || undefined);
    },
  });
  return null;
};

// Component to update map view when coordinates change from external input
const MapViewUpdater: React.FC<{ latitude: number; longitude: number }> = ({ latitude, longitude }) => {
  const map = useMap();

  useEffect(() => {
    map.setView([latitude, longitude], map.getZoom());
  }, [map, latitude, longitude]);

  return null;
};

const InteractiveMap = forwardRef<InteractiveMapRef, InteractiveMapProps>(({
  latitude,
  longitude,
  onLocationChange,
  height = '300px',
  showSearch = true,
  hideLocationButton = false,
}, ref) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    triggerAutoLocation: getCurrentLocation
  }));

  // Debounced search
  useEffect(() => {
    if (searchQuery.length < 3) {
      setSearchResults([]);
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      setError(null);
      
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=5&addressdetails=1`
        );
        
        if (!response.ok) {
          throw new Error('Search failed');
        }
        
        const data: SearchResult[] = await response.json();
        setSearchResults(data);
      } catch (err) {
        setError('Failed to search locations');
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const handleSearchResultClick = (result: SearchResult) => {
    const shortAddress = formatShortAddress(result.address) || result.display_name;
    onLocationChange(parseFloat(result.lat.toString()), parseFloat(result.lon.toString()), shortAddress);
    setSearchQuery(result.display_name); // Keep full name in search field for context
    setSearchResults([]);
  };

  const getCurrentLocation = async () => {
    setIsGettingLocation(true);
    setError(null);

    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser');
      setIsGettingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude: lat, longitude: lng } = position.coords;
        setIsGettingLocation(false);

        const result = await reverseGeocode(lat, lng);
        if (result.fullAddress) {
          setSearchQuery(result.fullAddress);
        }
        onLocationChange(lat, lng, result.shortAddress || undefined);
      },
      (error) => {
        setError('Unable to get your location. Please check your browser settings.');
        setIsGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };

  const customIcon = new L.Icon({
    iconUrl: 'data:image/svg+xml;base64,' + btoa(`
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M21 10C21 17 12 23 12 23S3 17 3 10C3 5.02944 7.02944 1 12 1C16.9706 1 21 5.02944 21 10Z" fill="#4F46E5" stroke="white" stroke-width="2"/>
        <circle cx="12" cy="10" r="3" fill="white"/>
      </svg>
    `),
    iconSize: [24, 24],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24],
  });

  return (
    <div className="interactive-map-container">
      {showSearch && (
        <div className="map-search-container">
          <div className="map-search-input-container">
            <div className="map-search-input-wrapper">
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for a location..."
                bg="rgba(255, 255, 255, 0.95)"
                border="2px solid rgba(163, 116, 63, 0.3)"
                borderRadius="12px"
                height="48px"
                fontSize="14px"
                pl="40px"
                _focus={{
                  borderColor: '#4F46E5',
                  boxShadow: '0 0 0 3px rgba(79, 70, 229, 0.1)',
                }}
              />
              <Search 
                size={18} 
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#6B7280'
                }}
              />
              {isSearching && (
                <Spinner
                  size="sm"
                  color="#4F46E5"
                  position="absolute"
                  right="12px"
                  top="50%"
                  transform="translateY(-50%)"
                />
              )}
            </div>
            
            {!hideLocationButton && (
              <Box
                as="button"
                onClick={getCurrentLocation}
                disabled={isGettingLocation}
                bg="linear-gradient(135deg, #4F46E5, #7C3AED)"
                color="white"
                borderRadius="12px"
                height="48px"
                width="48px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                transition="all 0.3s ease"
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(79, 70, 229, 0.3)',
                }}
                _active={{
                  transform: 'translateY(0)',
                }}
              >
                {isGettingLocation ? (
                  <Spinner size="sm" color="white" />
                ) : (
                  <Navigation size={18} />
                )}
              </Box>
            )}
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <Box
              position="absolute"
              top="60px"
              left={0}
              right={0}
              bg="white"
              borderRadius="12px"
              boxShadow="0 8px 25px rgba(0, 0, 0, 0.1)"
              border="1px solid rgba(163, 116, 63, 0.2)"
              zIndex={1000}
              maxHeight="200px"
              overflowY="auto"
            >
              {searchResults.map((result, index) => (
                <Box
                  key={index}
                  as="button"
                  width="100%"
                  textAlign="left"
                  p={3}
                  borderBottom={index < searchResults.length - 1 ? "1px solid #F3F4F6" : "none"}
                  _hover={{
                    bg: 'rgba(79, 70, 229, 0.05)',
                  }}
                  onClick={() => handleSearchResultClick(result)}
                >
                  <HStack spacing={2}>
                    <MapPin size={16} color="#6B7280" />
                    <Text fontSize="14px" color="#374151" noOfLines={2}>
                      {result.display_name}
                    </Text>
                  </HStack>
                </Box>
              ))}
            </Box>
          )}
        </div>
      )}

      {error && (
        <Alert status="error" mb={3} borderRadius="8px">
          <AlertIcon />
          <Text fontSize="14px">{error}</Text>
        </Alert>
      )}

      <Box
        borderRadius="12px"
        overflow="hidden"
        border="2px solid rgba(163, 116, 63, 0.2)"
        boxShadow="0 4px 20px rgba(0, 0, 0, 0.1)"
        height={height}
      >
        <MapContainer
          center={[latitude, longitude]}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker position={[latitude, longitude]} icon={customIcon}>
            <Popup>
              <VStack spacing={1} align="start">
                <Text fontSize="12px" fontWeight="600">Selected Location</Text>
                <Text fontSize="11px" color="#6B7280">
                  {latitude.toFixed(6)}°, {longitude.toFixed(6)}°
                </Text>
              </VStack>
            </Popup>
          </Marker>
          <MapClickHandler onLocationChange={onLocationChange} />
          <MapViewUpdater latitude={latitude} longitude={longitude} />
        </MapContainer>
      </Box>

      <Text fontSize="12px" color="#6B7280" mt={2} textAlign="center">
        Click anywhere on the map to select coordinates
      </Text>
    </div>
  );
});

InteractiveMap.displayName = 'InteractiveMap';

export default InteractiveMap;
export type { InteractiveMapRef };